import { useEffect, useState } from 'react';
import { Calendar, Clock, Warning } from 'shared/icons';
import api from '../../services/api';
import { FormatIsoDate } from '../../utils/date';
import { CountLabel } from '../../utils/format';
import ErrorCard from '../ui/ErrorCard';
import Select from '../ui/Select';
import { Pill } from '../campaigns/campaignCardShared';
import { CampaignTypePill, FormatDaysUntil, IntelLoading } from './intelligenceShared';

/**
 * CALENDÁRIO de campanhas (Fase 4) — a agenda de vigências no horizonte
 * escolhido, em três blocos: o que COMEÇA, o que VENCE e os PRAZOS de adesão
 * (deadline de opt-in). O briefing mostra só o urgente (≤5 dias); aqui é o
 * planejamento da semana/quinzena/mês.
 */

const HORIZON_OPTIONS = [
    { value: 7, label: 'Próximos 7 dias' },
    { value: 15, label: 'Próximos 15 dias' },
    { value: 30, label: 'Próximos 30 dias' },
];

const CampaignCalendar = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getMarketIntelligenceCalendar({ days });
                if (!cancelled) {
                    setData(payload || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o calendário.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [days, refreshTick]);

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar o calendário"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }

    const starting = Array.isArray(data?.starting) ? data.starting : [];
    const finishing = Array.isArray(data?.finishing) ? data.finishing : [];
    const deadlines = Array.isArray(data?.deadlines) ? data.deadlines : [];

    return (
        <div className="space-y-4">
            <div className="w-52">
                <Select label="Horizonte" value={days} onChange={(event) => setDays(Number(event.target.value))}>
                    {HORIZON_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            </div>

            {loading ? (
                <IntelLoading text="Carregando calendário…" />
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <CalendarColumn
                        title="Começando"
                        icon={Calendar}
                        accent="text-info-600 dark:text-info-400"
                        rows={starting}
                        emptyMessage="Nenhuma campanha começa no horizonte."
                    />
                    <CalendarColumn
                        title="Vencendo"
                        icon={Warning}
                        accent="text-danger-600 dark:text-danger-400"
                        rows={finishing}
                        emptyMessage="Nenhuma campanha vence no horizonte."
                    />
                    <CalendarColumn
                        title="Prazos de adesão"
                        icon={Clock}
                        accent="text-warning-600 dark:text-warning-400"
                        rows={deadlines}
                        emptyMessage="Nenhum prazo de adesão no horizonte."
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Coluna do calendário. O par que decide ação é `days_until` (quando) +
 * `items_count` (tamanho do impacto) — os dois ganham o destaque visual.
 */
const CalendarColumn = ({ title, icon: Icon, accent, rows, emptyMessage }) => (
    <div className="rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
        <div className="flex items-center gap-2 border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
            <Icon size={16} className={accent} />
            <p className="text-sm font-semibold text-black dark:text-white">{title}</p>
            <span className="ml-auto rounded-full bg-mono-100 px-2 py-0.5 text-xs font-semibold text-mono-600 dark:bg-mono-800 dark:text-mono-300">
                {rows.length}
            </span>
        </div>
        {rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-mono-500">{emptyMessage}</p>
        ) : (
            <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                {rows.map((row) => (
                    <li key={row.campaign_id} className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                            <p className="min-w-0 flex-1 truncate text-sm font-medium text-black dark:text-white" title={row.name || ''}>
                                {row.name || row.campaign_id}
                            </p>
                            <Pill tone={Number(row.days_until) <= 1 ? 'danger' : 'info'} className="shrink-0">
                                {FormatDaysUntil(row.days_until)}
                            </Pill>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-mono-500">
                            <CampaignTypePill type={row.type} />
                            <span>{FormatIsoDate(row.date, { blank: '—' })}</span>
                            {/* CountLabel já pluraliza ("1 item"/"2 itens") — nada inline. */}
                            <span className="ml-auto tabular-nums">{CountLabel(row.items_count || 0, 'item', 'itens')}</span>
                        </div>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

export default CampaignCalendar;
